import { PortalButtonData, ModuleButtonData, ModuleMenuScreenCategoryData, ModuleMenuScreenData } from "../menuData";
import { mod } from "../../core/math";
import { MainMenuCache } from "../mainMenuCache";

enum KEYS
{
    TAB = 9,
    ARROW_LEFT = 37,
    ARROW_UP = 38,
    ARROW_RIGHT = 39,
    ARROW_DOWN = 40
}

enum DIRECTION
{
    FORWARD,
    BACKWARD
}

interface FocusMoveDefinition
{
    readonly nextFocusArea: GenericFocusArea;
    readonly direction: DIRECTION;
    readonly skipCount: number;
}

interface GenericFocusArea
{
    readonly buttonMap: Map<any, HTMLElement>;

    getFirstFocusItem(skipCount: number): any | null;
    getKey(data: any): any;
}

interface FocusArea<TData, TKey> extends GenericFocusArea
{
    readonly buttonMap: Map<TKey, HTMLElement>;

    getFocusMoveDefinition(e: KeyboardEvent): FocusMoveDefinition | null;
    getFirstFocusItem(skipCount: number): TData | null;
    getKey(data: TData): TKey;
}

/**
 * A Keyboard Event handler for moving focus around the Main Menu.
 */
// Ideally, this logic would be encapsulated within Vue, but it is handled externally due to complexity.
export type FocusHandler = ReturnType<typeof createFocusHandler>;

export function createFocusHandler(cache: MainMenuCache)
{
    const _portalFocusArea = getPortalFocusArea();
    const _moduleFocusArea = getModuleFocusArea();
    const _categoryFocusArea = getCategoryFocusArea();
    const _screenFocusArea = getScreenFocusArea();

    let _selectedCategory: ModuleMenuScreenCategoryData | null = null;

    const focusHandler =
    {
        setPortalButton: (portalButtonData: PortalButtonData, el: HTMLElement | null | undefined) => setButton(_portalFocusArea, portalButtonData, el),
        setModuleButton: (moduleButtonData: ModuleButtonData, el: HTMLElement | null | undefined) => setButton(_moduleFocusArea, moduleButtonData, el),
        setCategoryButton: (moduleMenuScreenCategoryData: ModuleMenuScreenCategoryData, el: HTMLElement | null | undefined) => setButton(_categoryFocusArea, moduleMenuScreenCategoryData, el),
        setScreenButton: (moduleMenuScreenData: ModuleMenuScreenData, el: HTMLElement | null | undefined) => setButton(_screenFocusArea, moduleMenuScreenData, el),

        setSelectedCategory: (moduleMenuScreenCategoryData: ModuleMenuScreenCategoryData) => _selectedCategory = moduleMenuScreenCategoryData,

        focusFirstItem: focusFirstItem,
        moveFocusFromPortal: moveFocusFromPortal,
        moveFocusFromModule: moveFocusFromModule,
        moveFocusFromCategory: moveFocusFromCategory,
        moveFocusFromScreen: moveFocusFromScreen
    };

    return focusHandler;

    function focusFirstItem(): void
    {
        const firstFocusElement = getFirstFocusElementInFocusArea(_portalFocusArea, 0);
        if (firstFocusElement === null)
        {
            return;
        }

        firstFocusElement.focus();
    }

    function moveFocusFromPortal(portalButtonData: PortalButtonData, e: KeyboardEvent): boolean
    {
        return internalMoveFocus(portalButtonData, cache.portalButtons, _portalFocusArea, e);
    }

    function moveFocusFromModule(moduleButtonData: ModuleButtonData, e: KeyboardEvent): boolean
    {
        return internalMoveFocus(moduleButtonData, cache.selectedModuleButtonDatas, _moduleFocusArea, e);
    }

    function moveFocusFromCategory(categoryData: ModuleMenuScreenCategoryData, e: KeyboardEvent): void
    {
        const currentModuleCategories = getCurrentModuleCategories();
        if (currentModuleCategories === null)
        {
            return;
        }
        
        internalMoveFocus(categoryData, currentModuleCategories, _categoryFocusArea, e);
    }

    function moveFocusFromScreen(screenData: ModuleMenuScreenData, e: KeyboardEvent): boolean
    {
        const currentModuleCategories = getCurrentModuleCategories();
        if (currentModuleCategories === null)
        {
            return false;
        }

        const currentCategory = currentModuleCategories.find((category) => category.menus.some(menu => menu.menuScreenID === screenData.menuScreenID));
        if (currentCategory === undefined)
        {
            return false;
        }

        return internalMoveFocus(screenData, currentCategory.menus, _screenFocusArea, e);
    }

    function internalMoveFocus<TData extends object, TKey>(data: TData, dataSource: TData[], focusArea: FocusArea<TData, TKey>, e: KeyboardEvent): boolean
    {
        const key = focusArea.getKey(data);
        const focusMoveDefinition = focusArea.getFocusMoveDefinition(e);
        if (focusMoveDefinition === null)
        {
            return false;
        }

        const stayInCurrentFocusArea = focusArea === focusMoveDefinition.nextFocusArea;

        const elementToFocus = stayInCurrentFocusArea
            ? getNextElementInFocusArea(key, dataSource, focusMoveDefinition.direction, focusMoveDefinition.skipCount, focusArea)
            : getFirstFocusElementInFocusArea(focusMoveDefinition.nextFocusArea, focusMoveDefinition.skipCount);

        if (elementToFocus !== null)
        {
            elementToFocus.focus();
            return true;
        }
        return false;
    }

    function getNextElementInFocusArea<TData extends object, TKey>(key: TKey, dataSource: TData[], direction: DIRECTION, skipCount: number, focusArea: FocusArea<TData, TKey>): HTMLElement | null
    {
        let delta = 0;
        switch (direction)
        {
            case DIRECTION.FORWARD:
                delta = 1;
                break;
            case DIRECTION.BACKWARD:
                delta = -1;
                break;
            default:
                return null;
        }

        const dataToFocus = getFromCollection(dataSource, (item) => focusArea.getKey(item) === key, delta + skipCount) || dataSource[0];
        const elementToFocus = focusArea.buttonMap.get(focusArea.getKey(dataToFocus));

        return elementToFocus || null;
    }

    function getFirstFocusElementInFocusArea(focusArea: GenericFocusArea, skipCount: number): HTMLElement | null
    {
        const selectedItem = focusArea.getFirstFocusItem(skipCount);
        if (selectedItem === null)
        {
            return null;
        }

        return focusArea.buttonMap.get(focusArea.getKey(selectedItem)) || null;
    }

    function getPortalFocusArea(): FocusArea<PortalButtonData, string>
    {
        const focusArea =
        {
            buttonMap: new Map<string, HTMLElement>(),

            getFocusMoveDefinition: function(e: KeyboardEvent): FocusMoveDefinition | null
            {
                switch (e.keyCode)
                {
                    case KEYS.TAB:
                        return { nextFocusArea: e.shiftKey ? _categoryFocusArea : _moduleFocusArea, direction: e.shiftKey ? DIRECTION.BACKWARD : DIRECTION.FORWARD, skipCount: 0 };
                    case KEYS.ARROW_LEFT:
                        return { nextFocusArea: _portalFocusArea, direction: DIRECTION.BACKWARD, skipCount: 0 };
                    case KEYS.ARROW_RIGHT:
                        return { nextFocusArea: _portalFocusArea, direction: DIRECTION.FORWARD, skipCount: 0 };
                    default:
                        return null;
                }
            },
            getFirstFocusItem: function(skipCount: number): PortalButtonData | null
            {
                return getFromCollection(cache.portalButtons, (portalButtonData) => portalButtonData.portalCode === cache.selectedPortalCode, skipCount);
            },
            getKey(data: PortalButtonData): string
            {
                return data.portalCode!;
            }
        };

        return focusArea;
    }

    function getModuleFocusArea(): FocusArea<ModuleButtonData, number>
    {
        const focusArea =
        {
            buttonMap: new Map<number, HTMLElement>(),

            getFocusMoveDefinition: function (e: KeyboardEvent): FocusMoveDefinition | null
            {
                switch (e.keyCode)
                {
                    case KEYS.TAB:
                        return { nextFocusArea: e.shiftKey ? _portalFocusArea : _categoryFocusArea, direction: e.shiftKey ? DIRECTION.BACKWARD : DIRECTION.FORWARD, skipCount: 0 };
                    case KEYS.ARROW_UP:
                        return { nextFocusArea: _moduleFocusArea, direction: DIRECTION.BACKWARD, skipCount: 0 };
                    case KEYS.ARROW_DOWN:
                        return { nextFocusArea: _moduleFocusArea, direction: DIRECTION.FORWARD, skipCount: 0 };
                    default:
                        return null;
                }
            },
            getFirstFocusItem: function (skipCount: number): ModuleButtonData | null
            {
                return getFromCollection(cache.selectedModuleButtonDatas, (moduleButtonData) => moduleButtonData.moduleID === cache.selectedModuleID, skipCount);
            },
            getKey(data: ModuleButtonData): number
            {
                return data.moduleID;
            }
        };

        return focusArea;
    }

    function getCategoryFocusArea(): FocusArea<ModuleMenuScreenCategoryData,  number>
    {
        const focusArea =
        {
            buttonMap: new Map<number, HTMLElement>(),

            getFocusMoveDefinition: function (e: KeyboardEvent): FocusMoveDefinition | null
            {
                switch (e.keyCode)
                {
                    case KEYS.TAB:
                        return { nextFocusArea: e.shiftKey ? _moduleFocusArea : _portalFocusArea, direction: e.shiftKey ? DIRECTION.BACKWARD : DIRECTION.FORWARD, skipCount: 0 };
                    case KEYS.ARROW_LEFT:
                        return { nextFocusArea: _categoryFocusArea, direction: DIRECTION.BACKWARD, skipCount: 0 };
                    case KEYS.ARROW_RIGHT:
                        return { nextFocusArea: _categoryFocusArea, direction: DIRECTION.FORWARD, skipCount: 0 };
                    case KEYS.ARROW_UP:
                        return { nextFocusArea: _screenFocusArea, direction: DIRECTION.BACKWARD, skipCount: 0 };
                    case KEYS.ARROW_DOWN:
                        return { nextFocusArea: _screenFocusArea, direction: DIRECTION.FORWARD, skipCount: 0 };
                    default:
                        return null;
                }
            },
            getFirstFocusItem: function (skipCount: number): ModuleMenuScreenCategoryData | null
            {
                const currentModuleCategories = getCurrentModuleCategories();
                if (currentModuleCategories === null || currentModuleCategories.length === 0)
                {
                    return null;
                }

                return getFromCollection(currentModuleCategories, (data) => data.menuCategoryID === _selectedCategory?.menuCategoryID, skipCount) || currentModuleCategories[0];
            },
            getKey: function (data: ModuleMenuScreenCategoryData)
            {
                return data.menuCategoryID;
            }
        };

        return focusArea;
    }

    function getScreenFocusArea(): FocusArea<ModuleMenuScreenData, number>
    {
        const focusArea =
        {
            buttonMap: new Map<number, HTMLElement>(),

            getFocusMoveDefinition: function (e: KeyboardEvent): FocusMoveDefinition | null
            {
                switch (e.keyCode)
                {
                    case KEYS.TAB:
                        return { nextFocusArea: e.shiftKey ? _moduleFocusArea : _portalFocusArea, direction: e.shiftKey ? DIRECTION.BACKWARD : DIRECTION.FORWARD, skipCount: 0 };
                    case KEYS.ARROW_LEFT:
                        return { nextFocusArea: _categoryFocusArea, direction: DIRECTION.BACKWARD, skipCount: -1 };
                    case KEYS.ARROW_RIGHT:
                        return { nextFocusArea: _categoryFocusArea, direction: DIRECTION.FORWARD, skipCount: 1 };
                    case KEYS.ARROW_UP:
                        return { nextFocusArea: _screenFocusArea, direction: DIRECTION.BACKWARD, skipCount: 0 };
                    case KEYS.ARROW_DOWN:
                        return { nextFocusArea: _screenFocusArea, direction: DIRECTION.FORWARD, skipCount: 0 };
                    default:
                        return null;
                }
            },
            getFirstFocusItem: function (_skipCount: number): ModuleMenuScreenData | null
            {
                const selectedCategory = _categoryFocusArea.getFirstFocusItem(0);
                if (selectedCategory === null || selectedCategory.menus.length === 0)
                {
                    return null;
                }

                // You cannot have a selected Screen, so return the first
                return selectedCategory.menus[0];
            },
            getKey(data: ModuleMenuScreenData)
            {
                return data.menuScreenID;
            }
        };

        return focusArea;
    }

    function getFromCollection<TData>(collection: TData[], testFunc: (item: TData) => boolean, skipCount: number): TData | null
    {
        let itemIndex = collection.findIndex(testFunc);
        if (itemIndex === -1)
        {
            return null;
        }

        itemIndex = mod(itemIndex + skipCount, collection.length);
        return collection[itemIndex] || null;
    }

    function getCurrentModuleCategories(): ModuleMenuScreenCategoryData[] | null
    {
        if (cache.selectedModuleID === null)
        {
            return null;
        }

        return cache.portals[cache.selectedPortalCode]?.modules[cache.selectedModuleID]?.categories || null;
    }

    function setButton<TData, TKey>(focusHandler: FocusArea<TData, TKey>, data: TData, element: HTMLElement | null | undefined)
    {
        const key = focusHandler.getKey(data);
        if (element === undefined || element === null)
        {
            focusHandler.buttonMap.delete(key);
        }
        else
        {
            focusHandler.buttonMap.set(key, element);
        }
    }
};